import { VpButton, VpIcon } from '@vtmn-play/react';
import clsx from 'clsx';
import { useStepper } from 'headless-stepper';
import { Fragment } from 'react';
import { StepperProps } from './stepper.props';
import styles from './styles.module.scss';

export const Stepper = ({
  steps,
  currentStep,
  nextButtonLabel = 'Next',
  previousButtonLabel = 'Back',
  saveButtonLabel = 'Save & create',
  save,
  canSave = () => false,
}: StepperProps) => {
  const { state, nextStep, prevStep, stepperProps } = useStepper({
    steps,
    currentStep,
  });
  const showPreviousButton =
    steps[state.currentStep].hasPreviousButton && state.currentStep > 0;
  const showNextButton =
    steps[state.currentStep].hasNextButton &&
    state.currentStep + 1 < state.totalSteps;
  const showSaveButton = state.currentStep + 1 === state.totalSteps;

  const isGoNextDisabled = () => {
    if (steps[state.currentStep].canGoNext === undefined) {
      return false;
    } else {
      return !steps[state.currentStep].canGoNext?.();
    }
  };

  return (
    <div data-testid="stepper" className="vtmn-flex vtmn-flex-col vtmn-gap-9">
      <nav
        className="vtmn-flex vtmn-justify-between vtmn-items-center"
        {...stepperProps}
      >
        {steps?.map((step, index) => {
          const { label } = step;
          const isPastStep = state.currentStep > index;
          const isCurrentStep = state.currentStep === index;
          const isGreenLine = state.currentStep >= index;

          return (
            <Fragment key={`fragment-${index}`}>
              {index > 0 && (
                <div
                  key={`line-${index}`}
                  className={clsx(
                    'vtmn-w-2/12',
                    `${styles['spo-stepper-line']}`,
                    {
                      [`${styles['spo-stepper-line_past']}`]: isGreenLine,
                    },
                  )}
                />
              )}
              <div
                key={`step-${index}`}
                className={clsx(styles['spo-stepper-step'], {
                  [styles['spo-stepper-step_current']]: isCurrentStep,
                  [styles['spo-stepper-step_past']]: isPastStep,
                })}
              >
                <div
                  key={`step-index-${index}`}
                  className={clsx(
                    'vtmn-flex',
                    'vtmn-justify-center',
                    'vtmn-items-center',
                    'vtmn-text-center',
                    `${styles['spo-stepper-step-index']}`,
                    {
                      [styles['spo-stepper-step-index_current']]: isCurrentStep,
                    },
                    {
                      [styles['spo-stepper-step-index_past']]: isPastStep,
                    },
                  )}
                >
                  {isPastStep ? (
                    <VpIcon key={`check-${index}`} name="check" size={16} />
                  ) : (
                    index + 1
                  )}
                </div>
                <div
                  className={clsx(
                    styles['spo-stepper-step-label_not-current'],
                    {
                      [styles['spo-stepper-step-label_current']]: isCurrentStep,
                    },
                  )}
                  key={`step-label-${index}`}
                >
                  {label}
                </div>
              </div>
            </Fragment>
          );
        })}
      </nav>
      <div
        className={`${styles['spo-stepper-content']} vtmn-flex vtmn-flex-col vtmn-align-start vtmn-gap-8 vtmn-p-8`}
      >
        {steps[state.currentStep].contentTitle && (
          <h2
            className={styles['spo-stepper-content-title']}
            data-testid={`stepper_title_step-${state.currentStep}`}
          >
            {steps[state.currentStep].contentTitle}
          </h2>
        )}
        <div>{steps[state.currentStep].content}</div>
        <div
          className={`vtmn-flex vtmn-justify-end vtmn-items-start vtmn-gap-5`}
        >
          {showPreviousButton ? (
            <VpButton
              shape="squared"
              data-testid="stepper_button_previous-step"
              variant="secondary"
              onClick={prevStep}
              disabled={!state.hasPreviousStep}
            >
              {previousButtonLabel}
            </VpButton>
          ) : null}
          {showNextButton ? (
            <VpButton
              shape="squared"
              data-testid="stepper_button_next-step"
              onClick={nextStep}
              disabled={isGoNextDisabled()}
            >
              {nextButtonLabel}
            </VpButton>
          ) : null}
          {showSaveButton ? (
            <VpButton
              shape="squared"
              data-testid="stepper_button-save"
              onClick={save}
              disabled={!canSave()}
            >
              {saveButtonLabel}
            </VpButton>
          ) : null}
        </div>
      </div>
    </div>
  );
};
